import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input } from '@viapeople/components'
import type React from 'react'
import { type ChangeEvent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FileUploadModal } from '../../../components/FileUploadModal'
import type { FileUploadRequest } from '../../../components/FileUploadModal/types'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

const ALLOWED_FILE_TYPES = [
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/csv',
]

const FILE_EXTENSIONS = '.csv, .xls, .xlsx'

interface HierarchyUploadModalProps {
	isOpen: boolean
	onClose: () => void
}

export const HierarchyUploadModal: React.FC<HierarchyUploadModalProps> = ({ isOpen, onClose }) => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const queryClient = useQueryClient()
	const [hierarchyName, setHierarchyName] = useState('')
	const toast = useToast()

	const { mutate: hierarchyFileUploadRequest } = useMutation({
		...trpc.admin.hierarchyFiles.requestFileUpload.mutationOptions(),
		onError: () => {
			toast.showToast('Error hierarchy file upload. Please try again.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.hierarchyFiles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const { mutate: acknowledgeFileUpload } = useMutation({
		...trpc.admin.hierarchyFiles.acknowledgeFileUpload.mutationOptions(),
		onError: () => {
			toast.showToast('Error fetching hierarchy file fields. Please try again.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.hierarchyFiles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const { mutateAsync: processFileUpload } = useMutation({
		...trpc.admin.hierarchyFiles.processFile.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Hierarchy file uploaded successfully', 'info')
		},
		onError: () => {
			toast.showToast('Error processing hierarchy file. Please try again.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.hierarchyFiles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const uploadRequest: FileUploadRequest = {
		requestUpload: async (data) => {
			try {
				return await new Promise((resolve, reject) => {
					hierarchyFileUploadRequest(
						{
							...data,
							organizationId: organizationId as string,
						},
						{
							onSuccess: resolve,
							onError: reject,
						},
					)
				})
			} catch (error) {
				toast.showToast('Error uploading file. Please try again.', 'error')
				throw error
			}
		},
		acknowledgeUpload: async (data) => {
			try {
				return await new Promise((resolve, reject) => {
					acknowledgeFileUpload(
						{
							...data,
							organizationId: organizationId as string,
							hierarchyName: data.filename as string,
						},
						{
							onSuccess: resolve,
							onError: reject,
						},
					)
				})
			} catch (error) {
				toast.showToast('Error acknowledging file upload. Please try again.', 'error')
				throw error
			}
		},
		processFile: async (data: { fileId: string; columnMapping: Record<string, string> }) => {
			if (!hierarchyName) {
				throw new Error('Hierarchy name is required')
			}

			const response = await processFileUpload({
				...data,
				organizationId: organizationId as string,
				userExternalIdHeader: data.columnMapping.employeeId,
				primaryManagerIdHeader: data.columnMapping.primaryManagerId,
				...(data.columnMapping.secondaryManagerId && {
					highLevelManagerIdHeader: data.columnMapping.secondaryManagerId,
				}),
				...(data.columnMapping.coManagerIds && {
					coManagerIdsHeader: data.columnMapping.coManagerIds,
				}),
				hierarchyName,
			})

			if (!response.isValid) {
				const errors = response.errors
				const firstTen = errors.slice(0, 10)
				const remaining = errors.length > 10 ? errors.length - 10 : 0
				const errorMsg = [...firstTen, remaining > 0 ? `and ${remaining} more errors` : ''].filter(Boolean).join('\n')
				throw new Error(errorMsg)
			}
		},
	}

	return (
		<FileUploadModal
			isOpen={isOpen}
			onClose={onClose}
			onUploadSuccess={() => {
				setHierarchyName('')
			}}
			fileConfig={{
				allowedTypes: ALLOWED_FILE_TYPES,
				extensions: FILE_EXTENSIONS,
				maxSize: 2 * 1024 * 1024,
			}}
			columnMappingFields={[
				{ key: 'employeeId', label: 'Employee ID Column', required: true },
				{ key: 'primaryManagerId', label: 'Primary Manager ID Column', required: true },
				{ key: 'secondaryManagerId', label: 'Secondary Manager ID Column', required: false },
				{ key: 'coManagerIds', label: 'Co-Manager IDs Column', required: false },
			]}
			uploadRequest={uploadRequest}
			title='Upload Hierarchy'
			renderBeforeColumnMapping={() => (
				<Input
					label='Hierarchy Name'
					value={hierarchyName}
					onChange={(e: ChangeEvent<HTMLInputElement>) => setHierarchyName(e.target.value)}
					placeholder='Enter hierarchy name'
					className='mb-4'
					required
				/>
			)}
		/>
	)
}
